import React, { useId, useState } from 'react';

type SimulationAccordionProps = {
  title: React.ReactNode;
  ariaLabel: string;
  /** Optional content pinned to the right of the title (e.g. status badge). */
  headerExtra?: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
  /** Applied to the expandable body when open. */
  bodyClassName?: string;
  children: React.ReactNode;
};

/**
 * Collapsible section shell shared by simulation side-panel cards.
 */
const SimulationAccordion: React.FC<SimulationAccordionProps> = ({
  title,
  ariaLabel,
  headerExtra,
  defaultOpen = false,
  className = '',
  bodyClassName = '',
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();

  return (
    <section
      className={`overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-800 shadow-sm ${className}`}
      aria-label={ariaLabel}
    >
      <header
        className={`flex items-center gap-2 px-3 py-2.5 ${open ? 'border-b border-slate-100' : ''}`}
      >
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((prev) => !prev)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <span
            className={`shrink-0 text-slate-400 transition-transform ${open ? '' : '-rotate-90'}`}
            aria-hidden
          >
            <ChevronIcon />
          </span>
          <h3 className="min-w-0 truncate text-[11px] font-semibold tracking-[0.14em] text-slate-800">
            {title}
          </h3>
        </button>
        {headerExtra ? <div className="shrink-0">{headerExtra}</div> : null}
      </header>

      {open ? (
        <div id={panelId} role="region" aria-label={ariaLabel} className={bodyClassName}>
          {children}
        </div>
      ) : null}
    </section>
  );
};

const ChevronIcon: React.FC = () => (
  <svg
    className="h-3 w-3"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
  >
    <path d="M3 4.5 6 7.5 9 4.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default SimulationAccordion;
